import type { MediaItem } from './types';

const FALLBACK_WIDE_PHOTO_INDEXES = new Set([4, 8, 10, 11]);
const FALLBACK_PATTERN_LENGTH = 12;

export const MIN_LANDSCAPE_RATIO = 1.2;

const PANORAMA_RATIO = 1.9;
const MOSTLY_LANDSCAPE_SHARE = 0.75;
const FOUR_COLUMN_WIDE_RATIO = 1.35;
const THREE_COLUMN_WIDE_RATIO = 1.45;

type ColumnLayoutRule = {
  wideEveryNthLandscapeItem: number;
  wideRatioThreshold: number;
  wideEveryNthEligibleItem: number;
};

const COLUMN_LAYOUT_RULES: Record<number, ColumnLayoutRule> = {
  2: {
    wideEveryNthLandscapeItem: 5,
    wideRatioThreshold: FOUR_COLUMN_WIDE_RATIO,
    wideEveryNthEligibleItem: 4,
  },
  3: {
    wideEveryNthLandscapeItem: 6,
    wideRatioThreshold: THREE_COLUMN_WIDE_RATIO,
    wideEveryNthEligibleItem: 4,
  },
  4: {
    wideEveryNthLandscapeItem: 4,
    wideRatioThreshold: FOUR_COLUMN_WIDE_RATIO,
    wideEveryNthEligibleItem: 2,
  },
};

export function isFallbackWidePhoto(index: number): boolean {
  // Keeps the grid rhythm while image ratios are still loading.
  return FALLBACK_WIDE_PHOTO_INDEXES.has(index % FALLBACK_PATTERN_LENGTH);
}

export function shouldSpanWide(
  ratio: number,
  index: number,
  columns: number,
  landscapeShare: number,
): boolean {
  if (ratio < MIN_LANDSCAPE_RATIO) {
    return false;
  }
  if (ratio >= PANORAMA_RATIO) {
    return true;
  }

  const rule = COLUMN_LAYOUT_RULES[columns];
  if (!rule) {
    return false;
  }

  const isMostlyLandscape = landscapeShare > MOSTLY_LANDSCAPE_SHARE;

  // If almost every image is horizontal, ratio alone stops being useful:
  // place wide tiles by rhythm so the gallery does not become too dense.
  if (isMostlyLandscape) {
    return index % rule.wideEveryNthLandscapeItem === 0;
  }

  return (
    ratio > rule.wideRatioThreshold &&
    index % rule.wideEveryNthEligibleItem === 0
  );
}

export function countLandscapeItems(
  items: MediaItem[],
  ratios: ReadonlyMap<MediaItem['id'], number>,
): number {
  return items.filter(
    (item) => (ratios.get(item.id) ?? 0) >= MIN_LANDSCAPE_RATIO,
  ).length;
}
